import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useOrderComponents } from '@/hooks/useOrderComponents';
import { AssemblyChecklist, CHECKLIST_SECTIONS, normaliseProgress } from '@/components/AssemblyChecklist';
import { PrintBuildSheet } from '@/components/PrintBuildSheet';
import { AONotes } from '@/components/AONotes';
import { SignaturePad } from '@/components/SignaturePad';
import { HelpTooltip } from '@/components/HelpTooltip';
import { 
  Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowUpDown, Search, Clock, Wrench, TestTube, CheckCircle2, ClipboardCheck, Trash2, Monitor, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { isAdmin, isSuperAdmin as isSuperAdminRole, isTechnician as isTechnicianRole } from '@/lib/roles';
import { PIPELINE_STATUSES } from '@/lib/assemblyOrderStatus';
import { formatDate, debounce } from '@/lib/utils';
import { useReviewTimeline } from '@/hooks/useReviewTimeline';
import { useOpenPauses, assertPausesReviewed } from '@/lib/api/pauses';
import { PauseReviewList, PausedBadge } from '@/components/BuildPauses';
import { BuildAttribution } from '@/components/BuildAttribution';
import { DemoSetupBanner } from '@/lib/demoSetup';
import { ReviewBuildTimeline } from '@/components/BuildDuration';
import { ChecklistProgress } from '@/components/ChecklistProgress';
import { toast } from 'sonner';
import { writeAuditLog } from '@/lib/audit';
import { fifoReallocate } from '@/lib/fifoReallocate';
import { allocationDeductions, resolvePostingBom } from '@/lib/orderBom';

import { useCurrentUser } from '@/lib/useCurrentUser';

type SortField = 'created_at' | 'order_number' | 'status' | 'build_sku' | 'technician_id' | 'to_number' | 'reservation_id' | 'quantity';
type SortOrder = 'asc' | 'desc';

type BuildOrderStatus = 'pending' | 'building' | 'software' | 'testing' | 'awaiting_review' | 'completed';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any; selectColor: string; action: string }> = {
  pending:         { label: 'Pending',               color: 'bg-gray-100 text-gray-700',   icon: Clock,          selectColor: 'text-gray-600',  action: 'Start Building' },
  building:        { label: 'Building',              color: 'bg-blue-100 text-blue-800',   icon: Wrench,         selectColor: 'text-blue-600',  action: 'Start Software Installation' },
  'In Progress':   { label: 'Building',              color: 'bg-blue-100 text-blue-800',   icon: Wrench,         selectColor: 'text-blue-600',  action: 'Start Software Installation' },
  software:        { label: 'Software Installation', color: 'bg-indigo-100 text-indigo-800', icon: Monitor,      selectColor: 'text-indigo-600',action: 'Start Testing' },
  testing:         { label: 'Testing',               color: 'bg-purple-100 text-purple-800', icon: TestTube,     selectColor: 'text-purple-600',action: 'Submit for Review' },
  awaiting_review: { label: 'Awaiting Review',       color: 'bg-yellow-100 text-yellow-800', icon: ClipboardCheck, selectColor: 'text-yellow-600', action: 'Approve & Complete' },
  completed:       { label: 'Completed',             color: 'bg-green-100 text-green-800', icon: CheckCircle2,   selectColor: 'text-green-600', action: '' },
};

// Normalise DB status to workflow status
const normaliseStatus = (s: string): BuildOrderStatus => {
  if (s === 'In Progress') return 'pending'; // legacy status = not yet started in new workflow
  return (s as BuildOrderStatus) || 'pending';
};

const STATUS_FLOW: BuildOrderStatus[] = [...PIPELINE_STATUSES, 'completed'];


/** Auto-fills checklist items for the section that triggers the given status, if not already complete. */
/**
 * Auto-fill/rollback checklist items for all units across all sections
 * that precede or follow the target status.
 * Handles both old flat format and new multi-unit format.
 */
function buildAutoProgress(order: any, nextStatus: BuildOrderStatus): Record<string, any> | undefined {
  const STATUS_SECTION_MAP: Record<string, string[]> = {
    software: ['assembly'],
    testing: ['assembly', 'software'],
    awaiting_review: ['assembly', 'software', 'benchmarking', 'finishing', 'final_touches'],
    completed: ['assembly', 'software', 'benchmarking', 'finishing', 'final_touches'],
  };
  const sectionKeys = STATUS_SECTION_MAP[nextStatus] ?? [];
  const unitCount = Math.max(order.quantity || 1, 1);
  const raw = order.checklist_progress;
  const allUnits = normaliseProgress(raw, unitCount);
  let changed = false;

  for (const unitKey of Object.keys(allUnits)) {
    const unitProg = { ...allUnits[unitKey] };
    for (const section of CHECKLIST_SECTIONS) {
      if (sectionKeys.includes(section.key)) {
        for (const item of section.items) {
          if (!unitProg[item.key]) { unitProg[item.key] = true; changed = true; }
        }
      } else {
        for (const item of section.items) {
          if (unitProg[item.key]) { unitProg[item.key] = false; changed = true; }
        }
      }
    }
    allUnits[unitKey] = unitProg;
  }

  return changed ? allUnits : undefined;
}

export function AssemblyOrdersPage() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Admin review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [orderToReview, setOrderToReview] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState<any[]>([]);
  // Stage timeline, judged duration and pauses for the order under review.
  const review = useReviewTimeline(orderToReview);
  const { data: openPauses = [] } = useOpenPauses();
  // Optional closing remark from the reviewer; draft until saved or posted.
  const [reviewRemarks, setReviewRemarks] = useState('');

  // Build details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [orderToView, setOrderToView] = useState<any>(null);
  const [detailsTab, setDetailsTab] = useState<'components' | 'checklist' | 'notes'>('components');
  const [showManagerSignoff, setShowManagerSignoff] = useState(false);
  const [editingField, setEditingField] = useState<'order_number' | 'to_number' | null>(null);
  const [editValue, setEditValue] = useState('');

  const queryClient = useQueryClient();

  // Fetch notes when the review dialog opens
  useEffect(() => {
    if (!orderToReview?.id) { setReviewNotes([]); return; }
    const aoId = orderToReview.id;

    // AO notes
    supabase
      .from('assembly_order_notes')
      .select('id, note_text, created_at, author:technicians!created_by(name, alias, role)')
      .eq('ao_id', aoId)
      .order('created_at', { ascending: true })
      .then(({ data }) => setReviewNotes(data ?? []));
  }, [orderToReview?.id]);

  const { data: currentUser } = useCurrentUser();

  const userRole = currentUser?.role;
  const currentTechnicianId = currentUser?.technicianId;
  const isSuperAdmin = isSuperAdminRole(userRole);
  const canViewActions = isAdmin(userRole);
  const isTechnician = isTechnicianRole(userRole);
  // Kept local: 'manager' and 'management' as a pair appear only here, and this
  // page treats them as one group distinct from admins.
  const isManager = userRole === 'manager' || userRole === 'management';
  const canViewAllTechnicians = isAdmin(userRole) || isManager;

  // Fetch technicians for filter — managers only see their managed techs
  const { data: technicians } = useQuery({
    queryKey: ['technicians-list', currentTechnicianId, isManager],
    queryFn: async () => {
      let query = supabase
        .from('technicians')
        .select('id, name, initials')
        .eq('is_active', true)
        .order('name');
      if (isManager && currentTechnicianId) {
        query = query.eq('managed_by', currentTechnicianId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: canViewAllTechnicians,
  });

  // Manager's managed technician IDs for query filtering
  const managedTechIds = isManager ? (technicians?.map(t => t.id) ?? []) : [];

  // Fetch active build orders (exclude completed)
  const { data, isLoading } = useQuery({
    queryKey: ['active-assembly-orders', search, statusFilter, technicianFilter, page, pageSize, sortField, sortOrder, currentTechnicianId, isTechnician, isManager, managedTechIds],
    queryFn: async () => {
      let query = supabase
        .from('assembly_orders')
        .select(`
          *,
          build:builds(id, sku, description, comments),
          technician:technicians!technician_id(name, initials),
          reservation:reservations(id, reservation_code)
        `, { count: 'exact' })
        .in('status', PIPELINE_STATUSES);

      // Technicians only see their assigned build orders
      if (isTechnician && currentTechnicianId) {
        query = query.eq('technician_id', currentTechnicianId);
      }

      // Managers only see their managed technicians' orders
      if (isManager && managedTechIds.length > 0) {
        query = query.in('technician_id', managedTechIds);
      }

      if (search) {
        const like = `%${search}%`;
        const clauses = [
          `order_number.ilike.${like}`,
          `to_number.ilike.${like}`,
          `build_sku.ilike.${like}`,
          `serial_number.ilike.${like}`,
        ];

        // build_sku is nullable on the order row, and multi-unit serials live in the
        // serial_numbers map rather than serial_number. PostgREST can't reach either
        // from a top-level or(), so resolve matching ids first and filter on those.
        const [buildMatches, serialMatches] = await Promise.all([
          supabase.from('builds').select('id').ilike('sku', like).limit(200),
          supabase.from('assembly_order_serials').select('assembly_order_id').ilike('serial', like).limit(200),
        ]);
        if (buildMatches.error) throw buildMatches.error;
        if (serialMatches.error) throw serialMatches.error;

        const buildIds = [...new Set((buildMatches.data ?? []).map(b => b.id))];
        if (buildIds.length) clauses.push(`build_id.in.(${buildIds.join(',')})`);

        const serialOrderIds = [...new Set((serialMatches.data ?? []).map(r => r.assembly_order_id))];
        if (serialOrderIds.length) clauses.push(`id.in.(${serialOrderIds.join(',')})`);

        query = query.or(clauses.join(','));
      }

      if (statusFilter) {
        const dbStatus = statusFilter === 'building' ? 'In Progress' : statusFilter;
        query = query.eq('status', dbStatus);
      }

      // Apply technician filter for admins and managers
      if (technicianFilter && !isTechnician) {
        query = query.eq('technician_id', technicianFilter);
      }

      query = query.order(sortField, { ascending: sortOrder === 'asc' });
      
      if (pageSize !== 'all') {
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;
      if (error) {
        console.error('❌ Assembly orders query error:', error);
        throw error;
      }
      console.log(`✅ Assembly orders: ${data?.length} rows, count=${count}`);
      return { data, count };
    },
    enabled: isTechnician ? !!currentTechnicianId : true,
  });

  // Fetch summary counts by status
  const { data: statusCounts } = useQuery({
    queryKey: ['assembly-order-status-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select('status')
        .in('status', PIPELINE_STATUSES);
      
      if (error) throw error;
      
      const counts: Record<string, number> = {
        pending: 0,
        building: 0,
        software: 0,
        testing: 0,
        awaiting_review: 0,
      };
      
      data?.forEach(row => {
        const s = row.status === 'In Progress' ? 'pending' : row.status;
        if (s && counts[s] !== undefined) {
          counts[s]++;
        }
      });
      
      return counts;
    },
  });

  // Update status mutation
  const updateStatus = useMutation({
    mutationFn: async ({ id, newStatus, oldStatus, orderNumber, checklistProgress }: {
      id: string;
      newStatus: BuildOrderStatus;
      oldStatus?: string;
      orderNumber?: string;
      checklistProgress?: Record<string, boolean>;
    }) => {
      const updates: any = { status: newStatus };

      if (newStatus === 'building') {
        updates.started_at = new Date().toISOString();
      }

      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      }

      if (newStatus === 'pending') {
        updates.started_at = null;
        updates.completed_at = null;
      }

      if (checklistProgress) updates.checklist_progress = checklistProgress;

      const { data: updated, error } = await supabase
        .from('assembly_orders')
        .update(updates)
        .eq('id', id)
        .select('id, status')
        .single();

      if (error) throw error;
      if (!updated) throw new Error('Update was blocked — check your permissions or contact an admin.');

      writeAuditLog({
        action: 'assembly_order_status_changed',
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        old_value: { status: oldStatus },
        new_value: { status: newStatus },
      });
    },
    onSuccess: (_, { newStatus, checklistProgress }) => {
      // Update the dialog's local state so stepper reflects immediately
      if (orderToView) {
        setOrderToView((prev: any) => prev ? {
          ...prev,
          status: newStatus,
          ...(checklistProgress ? { checklist_progress: checklistProgress } : {}),
        } : prev);
      }
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['assembly-order-status-counts'] });
      queryClient.invalidateQueries({ queryKey: ['technician-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    },
  });

  // Super admin inline field edit mutation
  const updateField = useMutation({
    mutationFn: async ({ id, field, value, oldValue, orderNumber }: { id: string; field: 'order_number' | 'to_number'; value: string; oldValue?: string; orderNumber?: string }) => {
      const { error } = await supabase
        .from('assembly_orders')
        .update({ [field]: value || null })
        .eq('id', id);
      if (error) throw error;

      writeAuditLog({
        action: `assembly_order_field_updated`,
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        old_value: { [field]: oldValue ?? null },
        new_value: { [field]: value || null },
        notes: `Field '${field}' updated`,
      });
    },
    onSuccess: (_, { field, value }) => {
      setOrderToView((prev: any) => prev ? { ...prev, [field]: value || null } : prev);
      setEditingField(null);
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      toast.success('Updated successfully');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update'),
  });

  // Admin approve mutation
  const approveOrder = useMutation({
    mutationFn: async ({ id, to_number, legacy_iir, reservation_id, ao_quantity, orderNumber, build_id, review_remarks }: { id: string; to_number: string; legacy_iir: string; reservation_id?: string; ao_quantity: number; orderNumber?: string; build_id?: string; review_remarks?: string | null }) => {
      // Before anything else: stock is deducted below, ahead of the status
      // write the database would refuse.
      await assertPausesReviewed(id);

      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const aoQty = ao_quantity || 1;

      // Component list, reconciled with the reservation — see resolvePostingBom.
      const { bom, source: bomSource, diff: bomDiff } = await resolvePostingBom(id, reservation_id, aoQty);

      const skus = bom.map(c => c.sku);

      // Merge what this order actually used into the build's component list.
      //
      // This used to delete the build's components and re-insert only the
      // latest order's, which meant a swap under the same build SKU erased the
      // part it replaced — the build no longer listed a component it had
      // genuinely been assembled from. A build's set is the accumulation of
      // every component ever posted against it, so rows are added and
      // refreshed but never removed here. Retiring a component from a build
      // stays a deliberate edit on the build itself.
      if (build_id) {
        const { data: existingBc, error: exErr } = await supabase
          .from('build_components')
          .select('id, component_sku')
          .eq('build_id', build_id);
        if (exErr) throw exErr;

        const idBySku = new Map<string, string>(
          (existingBc ?? []).map((r: any) => [String(r.component_sku).trim().toLowerCase(), r.id]),
        );

        const newRows = bom.filter(c => !idBySku.has(String(c.sku).trim().toLowerCase()));
        if (newRows.length) {
          const { error: bcErr } = await supabase.from('build_components').insert(
            newRows.map(c => ({
              build_id,
              component_sku:  c.sku,
              component_type: c.type,
              qty_per_build:  c.qty / aoQty,
              cost_ex_vat:    c.cost,
            }))
          );
          if (bcErr) throw bcErr;
        }

        // Components this order did use get their quantity and cost refreshed;
        // ones it did not are left alone rather than dropped.
        for (const c of bom) {
          const rowId = idBySku.get(String(c.sku).trim().toLowerCase());
          if (!rowId) continue;
          const { error: updErr } = await supabase
            .from('build_components')
            .update({ qty_per_build: c.qty / aoQty, cost_ex_vat: c.cost })
            .eq('id', rowId);
          if (updErr) throw updErr;
        }

        writeAuditLog({
          action: 'build_bom_synced_from_order',
          entity_type: 'build',
          entity_id: build_id,
          entity_label: orderNumber,
          new_value: {
            source_ao_id: id,
            component_count: bom.length,
            added: newRows.length,
            refreshed: bom.length - newRows.length,
          },
        });
      }

      // Run FIFO first so any stock that has physically arrived but is still
      // sitting in expected_qty gets converted to allocated_qty before we
      // deduct. A build can only ever consume physical (allocated) stock —
      // expected_qty units are not in the warehouse yet.
      await fifoReallocate(skus);

      // Deduct physical stock (total_qty) consumed by the build.
      const { data: comps } = await supabase
        .from('components')
        .select('part_number, total_qty')
        .in('part_number', skus);

      if (comps?.length) {
        const compMap = Object.fromEntries(
          (comps as any[]).map(c => [c.part_number, c.total_qty ?? 0])
        );
        await Promise.all(
          bom.map(item => {
            const curTotal = compMap[item.sku] ?? 0;
            return supabase.from('components').update({
              total_qty: Math.max(0, curTotal - item.qty),
            }).eq('part_number', item.sku);
          })
        );
      }

      if (reservation_id) {
        // Deduct from allocated_qty only — expected_qty is not physical stock
        // and must never be consumed by a posted build.
        const { data: rcRows } = await supabase
          .from('reservation_components')
          .select('id, component_sku, allocated_qty')
          .eq('reservation_id', reservation_id);
        for (const u of allocationDeductions(rcRows ?? [], bom)) {
          await supabase.from('reservation_components').update({
            allocated_qty: u.allocated_qty,
          }).eq('id', u.id);
        }
      }

      // Reconcile reserved_qty / expected_qty / available_qty on all affected
      // components now that total_qty and RC allocated_qty are correct.
      await fifoReallocate(skus);

      // Update reservation fulfillment
      if (reservation_id) {
        const { data: res } = await supabase
          .from('reservations')
          .select('quantity, quantity_fulfilled')
          .eq('id', reservation_id)
          .single();
        if (res) {
          const newFulfilled = (res.quantity_fulfilled || 0) + aoQty;
          const newStatus = newFulfilled >= res.quantity ? 'Completed' : 'Partial';
          await supabase
            .from('reservations')
            .update({ quantity_fulfilled: newFulfilled, status: newStatus })
            .eq('id', reservation_id);
        }
      }

      // Write total cost back to assembly_orders
      const totalCostExVat = bom.reduce((sum, c) => sum + c.cost * c.qty, 0);

      // Note: completed_at is intentionally NOT overwritten here — it holds
      // the technician's "submitted for review" timestamp (set by the
      // TechnicianOrdersPage sign-off flow / AssemblyChecklist when the
      // order first moves to awaiting_review). The admin-posting moment is
      // captured separately in posted_date. Overwriting completed_at here
      // used to shift the order's "finish" time to the admin-approval time,
      // which broke the build-timeline upper bound for unmanaged techs.
      const { error } = await supabase
        .from('assembly_orders')
        .update({
          status: 'Posted',
          completed_date: today,
          to_number: to_number || null,
          legacy_iir: legacy_iir || null,
          posted_date: now.toISOString(),
          total_cost_ex_vat: totalCostExVat,
          // Only when the reviewer changed it, so posting never re-attributes
          // someone else's saved remark.
          ...(review_remarks !== undefined && {
            review_remarks,
            review_remarks_by: currentTechnicianId || null,
            review_remarks_at: now.toISOString(),
          }),
        })
        .eq('id', id);

      if (error) throw error;

      // Audit: component snapshot
      if (bomSource === 'created') {
        writeAuditLog({
          action: 'assembly_order_components_snapshot',
          entity_type: 'assembly_order',
          entity_id: id,
          entity_label: orderNumber,
          new_value: { component_count: bom.length, source: 'reservation' },
        });
      }
      if (bomSource === 'refreshed' && bomDiff) {
        writeAuditLog({
          action: 'assembly_order_components_refreshed',
          entity_type: 'assembly_order',
          entity_id: id,
          entity_label: orderNumber,
          old_value: { removed: bomDiff.removed },
          new_value: { added: bomDiff.added },
          notes: 'Order copy disagreed with its reservation at posting; posted with the reservation.',
        });
      }

      // Audit: stock deduction
      writeAuditLog({
        action: 'assembly_order_stock_deducted',
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        new_value: { skus_deducted: skus, quantity: ao_quantity },
      });

      // Audit: reservation fulfillment
      if (reservation_id) {
        writeAuditLog({
          action: 'assembly_order_reservation_fulfilled',
          entity_type: 'assembly_order',
          entity_id: id,
          entity_label: orderNumber,
          new_value: { reservation_id, units_fulfilled: aoQty },
        });
      }

      // Audit: order posted
      writeAuditLog({
        action: 'assembly_order_posted',
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        old_value: { status: 'awaiting_review' },
        new_value: {
          status: 'Posted', total_cost_ex_vat: totalCostExVat, to_number: to_number || null,
          ...(review_remarks !== undefined && { review_remarks }),
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['assembly-order-status-counts'] });
      queryClient.invalidateQueries({ queryKey: ['posted-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['reservations-enriched'] });
      queryClient.invalidateQueries({ queryKey: ['reservation'] });
      queryClient.invalidateQueries({ queryKey: ['components'] });
      queryClient.invalidateQueries({ queryKey: ['build-bom'] });
      toast.success('Assembly order posted successfully');
      setReviewDialogOpen(false);
      setOrderToReview(null);
    },
  });
  
    // Delete build order mutation
  const deleteOrder = useMutation({
    mutationFn: async ({ id, orderNumber, buildSku, status, technicianName, quantity }: { id: string; orderNumber?: string; buildSku?: string; status?: string; technicianName?: string; quantity?: number }) => {
      const { error } = await supabase
        .from('assembly_orders')
        .delete()
        .eq('id', id);

      if (error) throw error;

      writeAuditLog({
        action: 'assembly_order_deleted',
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        old_value: { order_number: orderNumber, build_sku: buildSku, status, technician: technicianName, quantity },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      toast.success('Assembly order deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete order: ${error.message}`);
    },
  });

  // Handler for delete confirmation
  const handleDelete = (order: any) => {
    if (confirm(
      `Are you sure you want to delete assembly order ${order.order_number}?\n\n` +
      `Build: ${order.build?.sku || 'Unknown'}\n` +
      `Quantity: ${order.quantity || 1} unit(s)\n\n` +
      `This action cannot be undone.`
    )) {
      deleteOrder.mutate({
        id: order.id,
        orderNumber: order.order_number,
        buildSku: order.build?.sku,
        status: order.status,
        technicianName: order.technician?.name,
        quantity: order.quantity,
      });
    }
  };


  const totalPages = pageSize === 'all' ? 1 : Math.ceil((data?.count || 0) / (pageSize as number));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleSearchChange = debounce((value: string) => {
    setSearch(value);
    setPage(1);
  }, 300);

  const getNextStatus = (currentStatus: BuildOrderStatus): BuildOrderStatus | null => {
    const currentIndex = STATUS_FLOW.indexOf(currentStatus);
    if (currentIndex < STATUS_FLOW.length - 1) {
      return STATUS_FLOW[currentIndex + 1];
    }
    return null;
  };

  const openReviewDialog = (order: any) => {
    setOrderToReview(order);
    setReviewRemarks(order.review_remarks || '');
    setReviewDialogOpen(true);
  };

  // The draft, as it would be stored — undefined when it matches what is saved.
  const changedRemarks = (): string | null | undefined => {
    const next = reviewRemarks.trim() || null;
    return next === (orderToReview?.review_remarks || null) ? undefined : next;
  };

  // Managers review but do not post, so remarks save on their own as well.
  const saveRemarks = useMutation({
    mutationFn: async ({ id, remarks, orderNumber }: { id: string; remarks: string | null; orderNumber?: string }) => {
      const at = new Date().toISOString();
      const { error } = await supabase
        .from('assembly_orders')
        .update({ review_remarks: remarks, review_remarks_by: currentTechnicianId || null, review_remarks_at: at })
        .eq('id', id);
      if (error) throw error;
      writeAuditLog({
        action: 'assembly_order_review_remarks_saved',
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        new_value: { review_remarks: remarks },
      });
      return { remarks, at };
    },
    onSuccess: ({ remarks, at }) => {
      setOrderToReview((prev: any) =>
        prev ? { ...prev, review_remarks: remarks, review_remarks_by: currentTechnicianId || null, review_remarks_at: at } : prev
      );
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      toast.success('Remarks saved');
    },
    onError: (err: any) => toast.error(`Failed to save remarks: ${err.message}`),
  });

  const openDetailsDialog = (order: any) => {
    setOrderToView(order);
    setDetailsTab('components');
    setDetailsDialogOpen(true);
  };

  const handleApprove = () => {
    if (orderToReview) {
      approveOrder.mutate({
        id:             orderToReview.id,
        to_number:      orderToReview.to_number || '',
        legacy_iir:     orderToReview.order_number,
        reservation_id: orderToReview.reservation_id,
        ao_quantity:    orderToReview.quantity || 1,
        orderNumber:    orderToReview.order_number,
        build_id:       orderToReview.build_id,
        review_remarks: changedRemarks(),
      });
    }
  };

  const STATUS_SORT: Record<string, number> = {
    building: 1, 'In Progress': 4, software: 2, testing: 3, pending: 4, awaiting_review: 5,
  };
  const sortedOrders = [...(data?.data ?? [])].sort((a, b) => {
    const pa = STATUS_SORT[a.status] ?? 4;
    const pb = STATUS_SORT[b.status] ?? 4;
    return pa - pb;
  });

  const SortHeader = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <th 
      className={`text-left p-4 font-medium cursor-pointer hover:bg-muted/70 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-4 w-4 ${sortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
    </th>
  );

  const { data: orderComponents, isLoading: loadingComponents } = useOrderComponents(orderToView);

  const totalActive = Object.values(statusCounts || {}).reduce((a, b) => a + b, 0);


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {isTechnician ? 'My Assembly Orders' : 'Assembly Orders'}
          </h1>
          <p className="text-muted-foreground">
            {totalActive} {isTechnician ? 'assigned to you' : 'builds in progress'}
          </p>
        </div>
        {canViewActions && (
          <Button asChild variant="outline">
            <Link to="/production">
              <Plus className="mr-2 h-4 w-4" />
              New Assembly Order
            </Link>
          </Button>
        )}
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {PIPELINE_STATUSES.map((status) => {
          const config = STATUS_CONFIG[status];
          const Icon = config.icon;
          const count = statusCounts?.[status] || 0;
          
          return (
            <Card 
              key={status} 
              className={`cursor-pointer transition-all ${statusFilter === status ? 'ring-2 ring-primary' : 'hover:bg-muted/50'}`}
              onClick={() => {
                if (statusFilter === status) {
                  setStatusFilter('');
                } else {
                  setStatusFilter(status);
                }
                setPage(1);
              }}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{config.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search order #, build SKU, Transfer Order, serial..."
                className="pl-9 pr-9"
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); handleSearchChange(e.target.value); }}
              />
              {searchInput && (
                <button onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Active</option>
              <option value="pending">Pending</option>
              <option value="building">Building</option>
              <option value="software">Software Installation</option>
              <option value="testing">Testing</option>
              <option value="awaiting_review">Awaiting Review</option>
            </select>

            {(canViewAllTechnicians || isManager) && (
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={technicianFilter}
                onChange={(e) => { setTechnicianFilter(e.target.value); setPage(1); }}
              >
                <option value="">{isManager ? 'All Managed' : 'All Technicians'}</option>
                {technicians?.map((tech) => (
                  <option key={tech.id} value={tech.id}>
                    {tech.name} ({tech.initials})
                  </option>
                ))}
              </select>
            )}

            {statusFilter && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setStatusFilter(''); setPage(1); }}
              >
                Clear filters
              </Button>
            )}

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">Show:</span>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={pageSize}
                onChange={(e) => { 
                  const val = e.target.value;
                  setPageSize(val === 'all' ? 'all' : Number(val)); 
                  setPage(1); 
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <SortHeader field="order_number">Order #</SortHeader>
                      <SortHeader field="build_sku">Build</SortHeader>
                      <SortHeader field="to_number">TO</SortHeader>
                      <SortHeader field="quantity" className="text-center">Qty</SortHeader>
                      <SortHeader field="reservation_id">Reservation</SortHeader>
                      <SortHeader field="technician_id">Technician</SortHeader>
                      <SortHeader field="status">Status</SortHeader>
                      <SortHeader field="created_at">Created</SortHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders.map((order: any) => {
                      const status = normaliseStatus(order.status);
                      const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                      return (
                        <tr
                          key={order.id}
                          className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => openDetailsDialog(order)}
                        >
                          <td className="p-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm font-medium text-primary">
                                {order.order_number}
                              </span>
                              {order.build_type && order.build_type !== 'Stock' && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">{order.build_type}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <p className="text-sm font-medium font-mono">{order.build?.sku || order.build_sku || '—'}</p>
                            {order.build?.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[250px]">{order.build.description}</p>
                            )}
                          </td>
                          <td className="p-4">
                            <span className="text-sm font-mono">{order.to_number || '—'}</span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="text-sm">{order.quantity || 1}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-sm font-mono">{order.reservation?.reservation_code || '—'}</span>
                          </td>
                          <td className="p-4">
                            {order.technician ? (
                              <span className="text-sm">{order.technician.name}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground italic">Unassigned</span>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge className={`text-xs ${config.color}`}>{config.label}</Badge>
                            <PausedBadge
                              pauses={openPauses.filter(p => p.assembly_order_id === order.id)}
                              quantity={order.quantity}
                              className="ml-1.5"
                            />
                            {/* Where inside the stage each unit is */}
                            {status !== 'pending' && (
                              <div className="mt-2 w-52">
                                <ChecklistProgress checklistProgress={order.checklist_progress} quantity={order.quantity} />
                              </div>
                            )}
                          </td>
                          <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                            {order.created_at ? formatDate(order.created_at) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {sortedOrders.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mb-2 text-green-500" />
                    <p>No active build orders</p>
                    <p className="text-sm">All builds are complete!</p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {pageSize !== 'all' && totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((page - 1) * (pageSize as number)) + 1} to {Math.min(page * (pageSize as number), data?.count || 0)} of {data?.count || 0}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {pageSize === 'all' && (
                <div className="px-4 py-3 border-t text-sm text-muted-foreground">
                  Showing all {data?.count || 0} orders
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Build Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {orderToView?.order_number} — {orderToView?.build?.sku || 'Assembly Order'}
            </DialogTitle>
            <DialogDescription>
              {orderToView?.build?.description || 'Build order details'}
            </DialogDescription>
          </DialogHeader>

          {orderToView && (() => {
            const viewStatus = normaliseStatus(orderToView.status);
            const viewConfig = STATUS_CONFIG[viewStatus] || STATUS_CONFIG.pending;
            const viewNextStatus = getNextStatus(viewStatus);
            return (
            <div className="space-y-4">
              {/* Order summary */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-muted rounded-lg text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">Order</span>
                  {isSuperAdmin && editingField === 'order_number' ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <input
                        className="h-7 px-2 text-sm font-mono border rounded bg-background w-full"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') updateField.mutate({ id: orderToView.id, field: 'order_number', value: editValue, oldValue: orderToView.order_number, orderNumber: orderToView.order_number });
                          if (e.key === 'Escape') setEditingField(null);
                        }}
                        autoFocus
                      />
                      <button onClick={() => updateField.mutate({ id: orderToView.id, field: 'order_number', value: editValue, oldValue: orderToView.order_number, orderNumber: orderToView.order_number })} className="text-green-600 hover:text-green-700" title="Save">✓</button>
                      <button onClick={() => setEditingField(null)} className="text-muted-foreground hover:text-foreground" title="Cancel">✕</button>
                    </div>
                  ) : (
                    <p
                      className={`font-mono font-medium ${isSuperAdmin ? 'cursor-pointer hover:text-primary' : ''}`}
                      onClick={() => { if (isSuperAdmin) { setEditingField('order_number'); setEditValue(orderToView.order_number || ''); } }}
                      title={isSuperAdmin ? 'Click to edit' : undefined}
                    >{orderToView.order_number}</p>
                  )}
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Build Type</span>
                  <p>{orderToView.build_type || 'Stock'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Quantity</span>
                  <p>{orderToView.quantity || 1} unit{(orderToView.quantity || 1) > 1 ? 's' : ''}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Reservation</span>
                  <p className="font-mono">{orderToView.reservation?.reservation_code || '—'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Technician <HelpTooltip tipKey="assemblyOrders.techAssignment" /></span>
                  <p>{orderToView.technician?.name || 'Unassigned'}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Transfer Order</span>
                  {isSuperAdmin && editingField === 'to_number' ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <input
                        className="h-7 px-2 text-sm font-mono border rounded bg-background w-full"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') updateField.mutate({ id: orderToView.id, field: 'to_number', value: editValue, oldValue: orderToView.to_number, orderNumber: orderToView.order_number });
                          if (e.key === 'Escape') setEditingField(null);
                        }}
                        autoFocus
                      />
                      <button onClick={() => updateField.mutate({ id: orderToView.id, field: 'to_number', value: editValue, oldValue: orderToView.to_number, orderNumber: orderToView.order_number })} className="text-green-600 hover:text-green-700" title="Save">✓</button>
                      <button onClick={() => setEditingField(null)} className="text-muted-foreground hover:text-foreground" title="Cancel">✕</button>
                    </div>
                  ) : (
                    <p
                      className={`font-mono ${isSuperAdmin ? 'cursor-pointer hover:text-primary' : ''}`}
                      onClick={() => { if (isSuperAdmin) { setEditingField('to_number'); setEditValue(orderToView.to_number || ''); } }}
                      title={isSuperAdmin ? 'Click to edit' : undefined}
                    >{orderToView.to_number || '—'}</p>
                  )}
                </div>
              </div>

              {/* Workflow stepper */}
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs font-medium text-muted-foreground">Status Flow</span>
                <HelpTooltip tipKey="assemblyOrders.statusFlow" />
              </div>
              {(() => {
                const isMultiUnit = (orderToView.quantity || 1) > 1;
                return (
                  <>
                    <div className="flex items-center gap-1 px-2">
                      {STATUS_FLOW.filter(s => s !== 'completed').map((step, idx, arr) => {
                        const stepConfig = STATUS_CONFIG[step];
                        const StepIcon = stepConfig.icon;
                        const currentIdx = STATUS_FLOW.indexOf(viewStatus);
                        const stepIdx = STATUS_FLOW.indexOf(step);
                        const isActive = step === viewStatus;
                        const isDone = stepIdx < currentIdx;
                        const canClick = !isMultiUnit && !isActive && !isTechnician;
                        return (
                          <div key={step} className="flex items-center flex-1">
                            <button
                              title={isMultiUnit ? stepConfig.label : isActive ? `Current: ${stepConfig.label}` : isDone ? stepConfig.label : `Move to: ${stepConfig.label}`}
                              disabled={updateStatus.isPending || !canClick}
                              onClick={() => canClick && updateStatus.mutate({ id: orderToView.id, newStatus: step, oldStatus: viewStatus, orderNumber: orderToView.order_number, checklistProgress: buildAutoProgress(orderToView, step) })}
                              className={`flex flex-col items-center gap-1 flex-1 transition-opacity ${!canClick ? 'cursor-default' : ''} ${isActive ? '' : isDone ? 'opacity-60' : 'opacity-25 hover:opacity-60'}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all
                                ${isActive ? `${stepConfig.color} border-current shadow-sm ring-2 ring-offset-1 ring-current` : isDone ? 'bg-green-100 text-green-700 border-green-300' : 'bg-muted text-muted-foreground border-border'}`}>
                                {isDone ? <CheckCircle2 className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                              </div>
                              <span className={`text-[10px] leading-tight text-center font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {step === 'software' ? 'Software' : step === 'awaiting_review' ? 'Review' : stepConfig.label}
                              </span>
                            </button>
                            {idx < arr.length - 1 && (
                              <div className={`h-0.5 flex-1 mx-1 rounded mb-4 ${isDone ? 'bg-green-300' : 'bg-border'}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {isMultiUnit && (
                      <p className="text-xs text-muted-foreground text-center italic">
                        Multi-unit order — status advances automatically via checklist completion
                      </p>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 justify-end">
                      {!isMultiUnit && canViewActions && viewNextStatus && viewNextStatus !== 'completed' && (
                        <Button size="sm" onClick={() => updateStatus.mutate({ id: orderToView.id, newStatus: viewNextStatus, oldStatus: viewStatus, orderNumber: orderToView.order_number, checklistProgress: buildAutoProgress(orderToView, viewNextStatus) })} disabled={updateStatus.isPending}>
                          {viewConfig.action}
                        </Button>
                      )}
                      {!isMultiUnit && isTechnician && viewNextStatus && viewNextStatus !== 'completed' && viewNextStatus !== 'awaiting_review' && (
                        <Button size="sm" onClick={() => updateStatus.mutate({ id: orderToView.id, newStatus: viewNextStatus, oldStatus: viewStatus, orderNumber: orderToView.order_number, checklistProgress: buildAutoProgress(orderToView, viewNextStatus) })} disabled={updateStatus.isPending}>
                          {viewConfig.action}
                        </Button>
                      )}
                      {!isMultiUnit && isTechnician && viewNextStatus === 'awaiting_review' && (
                        <Button size="sm" onClick={() => updateStatus.mutate({ id: orderToView.id, newStatus: 'awaiting_review', oldStatus: viewStatus, orderNumber: orderToView.order_number, checklistProgress: buildAutoProgress(orderToView, 'awaiting_review') })} disabled={updateStatus.isPending}>
                          Submit for Review <HelpTooltip tipKey="assemblyOrders.awaitingReview" />
                        </Button>
                      )}
                      {(canViewActions || isManager) && viewStatus === 'awaiting_review' && (
                        <Button size="sm" variant="default" onClick={() => { setDetailsDialogOpen(false); openReviewDialog(orderToView); }}>
                          <ClipboardCheck className="h-4 w-4 mr-1" /> Review & Post
                        </Button>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Demo PC setup banner */}
              {orderToView.build_type === 'Demo' && <DemoSetupBanner />}

              {/* Build Comments (from build definition) */}
              {orderToView.build?.comments && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Build Comments</p>
                  <p className="text-sm p-3 bg-blue-50 border border-blue-200 rounded-md whitespace-pre-wrap">
                    {orderToView.build.comments}
                  </p>
                </div>
              )}

              {/* Technician Notes */}
              {orderToView.notes && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</p>
                  <p className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-md whitespace-pre-wrap">
                    {orderToView.notes}
                  </p>
                </div>
              )}

              {/* Tab switcher */}
              <div className="flex border-b">
                <button
                  onClick={() => setDetailsTab('components')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    detailsTab === 'components'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Components
                </button>
                <button
                  onClick={() => setDetailsTab('checklist')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    detailsTab === 'checklist'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Checklist
                </button>
                <button
                  onClick={() => setDetailsTab('notes')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    detailsTab === 'notes'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Notes
                </button>
              </div>

              {/* Tab content */}
              {detailsTab === 'components' && (
                <>
                  {loadingComponents ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                  ) : (orderComponents?.length ?? 0) > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Part Number</th>
                            <th className="text-left p-3 font-medium">Description</th>
                            <th className="text-right p-3 font-medium">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderComponents!.map((comp, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="p-3 text-xs">
                                <Badge variant="outline">{comp.type}</Badge>
                              </td>
                              <td className="p-3 font-mono text-xs">{comp.sku}</td>
                              <td className="p-3 text-xs text-muted-foreground">{comp.description || '—'}</td>
                              <td className="p-3 text-right">{comp.qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No components found for this order</p>
                      <p className="text-sm">Components may not have been snapshotted yet</p>
                    </div>
                  )}
                </>
              )}

              {detailsTab === 'checklist' && (
                <AssemblyChecklist
                  order={orderToView}
                  readOnly={isTechnician}
                  onStatusChange={(newStatus, newProgress) => {
                    setOrderToView((prev: any) => prev ? {
                      ...prev,
                      status: newStatus,
                      checklist_progress: newProgress,
                    } : prev);
                  }}
                />
              )}

              {detailsTab === 'notes' && currentTechnicianId && (
                <AONotes aoId={orderToView.id} currentUserId={currentTechnicianId} />
              )}
            </div>
          );})()}

          <DialogFooter>
            {canViewActions && orderToView && (
              <Button size="sm" variant="ghost" className="mr-auto hover:bg-red-50 hover:text-red-600" onClick={(e) => { e.stopPropagation(); setDetailsDialogOpen(false); handleDelete(orderToView); }} title="Delete order">
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
            )}
            <PrintBuildSheet
              order={orderToView}
              components={orderComponents ?? []}
              canViewCosts={false}
            />
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="flex flex-col max-h-[90vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Review Assembly Order</DialogTitle>
            <DialogDescription>
              Review and post this build order.
            </DialogDescription>
          </DialogHeader>

          {orderToReview && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              {/* Order summary */}
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Order:</span>
                  <span className="font-mono text-sm font-medium">{orderToReview.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Build:</span>
                  <span className="text-sm">{orderToReview.build?.sku || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Technician:</span>
                  <span className="text-sm">{orderToReview.technician?.name || 'Unassigned'}</span>
                </div>
                {orderToReview.reservation && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Reservation:</span>
                    <span className="font-mono text-sm">{orderToReview.reservation.reservation_code}</span>
                  </div>
                )}
                {orderToReview.to_number && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Transfer Order:</span>
                    <span className="font-mono text-sm font-medium">{orderToReview.to_number}</span>
                  </div>
                )}
              </div>

              {/* Technician Notes */}
              {orderToReview.notes && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Technician Notes</p>
                  <p className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-md whitespace-pre-wrap">
                    {orderToReview.notes}
                  </p>
                </div>
              )}


              {/* Stage Timeline */}
              <ReviewBuildTimeline
                stages={review.stages}
                duration={review.duration}
                lunchMs={review.lunchMs}
                pendingPauses={review.unreviewedPauses}
              />

              {/* Pauses: each approved or rejected before posting */}
              <PauseReviewList
                order={orderToReview}
                pauses={review.pauses}
                canReview
                activeDays={review.activeDays}
              />

              {/* Who built each unit, who submitted and signed */}
              <BuildAttribution order={orderToReview} />

              {/* AO Notes */}
              {reviewNotes.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</p>
                  <div className="space-y-2">
                    {reviewNotes.map((n: any) => (
                      <div key={n.id} className="p-2 bg-muted rounded-md">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium">{n.author?.alias || n.author?.name || '—'}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{n.note_text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Review remarks (optional) */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Review Remarks <span className="normal-case tracking-normal font-normal">(optional)</span>
                </p>
                <textarea
                  className="w-full p-2 text-sm border rounded-md resize-none bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                  rows={3}
                  placeholder="Any final remarks on this order…"
                  value={reviewRemarks}
                  onChange={e => setReviewRemarks(e.target.value)}
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground">
                    {changedRemarks() !== undefined
                      ? (isManager ? 'Not saved yet' : 'Saved when you post, or save now')
                      : orderToReview.review_remarks_at
                        ? `Saved ${new Date(orderToReview.review_remarks_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                        : ''}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={changedRemarks() === undefined || saveRemarks.isPending}
                    onClick={() => saveRemarks.mutate({
                      id: orderToReview.id,
                      remarks: changedRemarks() ?? null,
                      orderNumber: orderToReview.order_number,
                    })}
                  >
                    {saveRemarks.isPending ? 'Saving…' : 'Save Remarks'}
                  </Button>
                </div>
              </div>

              {/* Technician signature (read-only display) */}
              {orderToReview.tech_signature_url && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Technician Signature</p>
                  <img
                    src={orderToReview.tech_signature_url}
                    alt="Tech signature"
                    className="border rounded-md max-h-20 bg-white"
                  />
                </div>
              )}

              {/* Manager sign-off */}
              {orderToReview.manager_signature_url && !showManagerSignoff ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Your Signature</p>
                  <img
                    src={orderToReview.manager_signature_url}
                    alt="Manager signature"
                    className="border rounded-md max-h-20 bg-white"
                  />
                  <button
                    className="text-xs text-primary underline"
                    onClick={() => setShowManagerSignoff(true)}
                  >
                    Re-sign
                  </button>
                </div>
              ) : showManagerSignoff ? (
                <SignaturePad
                  aoId={orderToReview.id}
                  type="manager"
                  signedBy={currentTechnicianId || ''}
                  onSigned={(url, signedAt) => {
                    // orderToReview is a snapshot taken when the dialog opened,
                    // so merge the new URL in directly - otherwise the block
                    // below falls back to the "Sign Off" button until the list
                    // query happens to refetch and the dialog is reopened.
                    setOrderToReview((prev: any) =>
                      prev ? { ...prev, manager_signature_url: url, manager_signed_at: signedAt } : prev
                    );
                    setShowManagerSignoff(false);
                    queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
                  }}
                  onCancel={() => setShowManagerSignoff(false)}
                />
              ) : (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Manager / QC Sign-Off</p>
                  <Button variant="outline" size="sm" onClick={() => setShowManagerSignoff(true)}>
                    Sign Off
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-shrink-0 pt-2 border-t">
            <Button variant="outline" onClick={() => { setReviewDialogOpen(false); setShowManagerSignoff(false); }}>
              {isManager ? 'Close' : 'Cancel'}
            </Button>
            {!isManager && review.unreviewedPauses > 0 && (
              <span className="mr-auto self-center text-xs text-amber-700 dark:text-amber-500">
                Approve or reject {review.unreviewedPauses === 1 ? 'the pause' : `all ${review.unreviewedPauses} pauses`} to post.
              </span>
            )}
            {!isManager && (
              <Button
                onClick={handleApprove}
                disabled={approveOrder.isPending || review.unreviewedPauses > 0}
              >
                {approveOrder.isPending ? 'Posting...' : 'Post'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
